import { useState, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { CheckCircle2, Clock, ShieldQuestion } from "lucide-react";
import { StudioLayout } from "../../components/layout/StudioLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { AddressAutocomplete } from "../../components/forms/AddressAutocomplete";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { FlyerUpload } from "../../components/forms/FlyerUpload";
import type { Id } from "convex/_generated/dataModel";

export function StudioProfilePage() {
  const { user, isLoading } = useCurrentUser();
  const updateStudioProfile = useMutation(api.users.updateStudioProfile);

  const [studioName, setStudioName] = useState("");
  const [studioDescription, setStudioDescription] = useState("");
  const [studioPhone, setStudioPhone] = useState("");
  const [studioWebsite, setStudioWebsite] = useState("");
  const [studioAddress, setStudioAddress] = useState("");
  const [addressCoords, setAddressCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [studioLogoStorageId, setStudioLogoStorageId] = useState<Id<"_storage"> | null>(null);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form from current user data
  useEffect(() => {
    if (!user) return;
    setStudioName(user.studioName ?? "");
    setStudioDescription((user as any).studioDescription ?? "");
    setStudioPhone((user as any).studioPhone ?? "");
    setStudioWebsite((user as any).studioWebsite ?? "");
    setStudioAddress(user.studioAddress ?? "");
    setStudioLogoStorageId((user as any).studioLogoStorageId ?? null);
  }, [user]);

  const handleSave = async () => {
    if (!studioName.trim()) { setError("Studio name is required."); return; }

    setSaving(true);
    setError(null);
    try {
      await updateStudioProfile({
        studioName: studioName.trim(),
        studioDescription: studioDescription.trim() || undefined,
        studioPhone: studioPhone.trim() || undefined,
        studioWebsite: studioWebsite.trim() || undefined,
        studioAddress: studioAddress.trim() || undefined,
        studioLatitude: addressCoords?.latitude,
        studioLongitude: addressCoords?.longitude,
        studioLogoStorageId: studioLogoStorageId ?? undefined,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setError(e.message ?? "Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <StudioLayout>
        <div className="py-12 text-center text-muted-foreground">Loading...</div>
      </StudioLayout>
    );
  }

  if (!user || user.accountType !== "studio") {
    return (
      <StudioLayout>
        <div className="py-12 text-center text-muted-foreground">This page is only available to studio accounts.</div>
      </StudioLayout>
    );
  }

  const verificationStatus = user.verificationStatus;
  const studioVerified = (user as any).studioVerified;

  return (
    <StudioLayout>
      <div className="space-y-8 max-w-xl">
        {/* Studio identity */}
        <section>
          <h2 className="font-semibold text-base text-foreground mb-4">Studio Identity</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Studio Logo</label>
              <FlyerUpload
                storageId={studioLogoStorageId}
                onUpload={(id) => setStudioLogoStorageId(id)}
                label="Click to upload studio logo"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Shown on events you post. JPG, PNG, or WebP, max 10MB.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Studio Name</label>
              <Input
                value={studioName}
                onChange={(e) => setStudioName(e.target.value)}
                placeholder="Your studio name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Description <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <Textarea
                value={studioDescription}
                onChange={(e) => setStudioDescription(e.target.value)}
                rows={4}
                placeholder="Tell dancers what makes your studio special — dance styles, vibe, level of experience..."
              />
            </div>
          </div>
        </section>

        {/* Contact info */}
        <section>
          <h2 className="font-semibold text-base text-foreground mb-4">Contact Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Website <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <Input
                value={studioWebsite}
                onChange={(e) => setStudioWebsite(e.target.value)}
                placeholder="https://yourstudio.com"
                type="url"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Phone <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <Input
                value={studioPhone}
                onChange={(e) => setStudioPhone(e.target.value)}
                placeholder="(555) 555-5555"
                type="tel"
              />
            </div>
          </div>
        </section>

        {/* Studio location */}
        <section>
          <h2 className="font-semibold text-base text-foreground mb-4">Studio Location</h2>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Address</label>
            <AddressAutocomplete
              value={studioAddress}
              onChange={(addr) => {
                setStudioAddress(addr);
                setAddressCoords(null);
              }}
              onPlaceSelect={(place) => {
                setStudioAddress(place.address);
                setAddressCoords(place.coords);
              }}
              placeholder="Start typing your studio address..."
            />
            <p className="text-xs text-muted-foreground mt-1">
              Used to help dancers find your studio on the map.
            </p>
          </div>
        </section>

        {/* Save */}
        {error && (
          <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{error}</p>
        )}
        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Profile"}
          </Button>
          {saved && (
            <span className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" />
              Saved!
            </span>
          )}
        </div>

        {/* Verification status (read-only) */}
        <section className="pt-4 border-t border-border">
          <h2 className="font-semibold text-base text-foreground mb-3">Verification Status</h2>
          <div className="flex items-start gap-3">
            {studioVerified ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            ) : verificationStatus === "pending" ? (
              <Clock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            ) : (
              <ShieldQuestion className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            )}
            <div>
              {studioVerified ? (
                <>
                  <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Verified Studio</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Your venue is verified. Events you post at your venue are auto-approved.
                  </p>
                </>
              ) : verificationStatus === "pending" ? (
                <>
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Verification Pending</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Your venue claim is under review by our team. We'll notify you when it's approved.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-foreground">Not Yet Verified</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Register or claim a venue from the Overview tab to start the verification process.
                  </p>
                </>
              )}
            </div>
          </div>
        </section>
      </div>
    </StudioLayout>
  );
}
